import { QontyEmptyState } from "@components/QontyEmptyState";
import { QontyLoading } from "@components/QontyLoading";
import {
    NovedadesExportService,
    NovedadesExportServiceModel,
} from "@services/NovedadesExportService";
import { RefreshButton } from "@shared/designSystem/Button";
import {
    HorizontalContainer,
    TableCell,
    TableHeaderCell,
    TableHeaderContainer,
    TableRowContainer,
    VerticalContainer,
} from "@shared/designSystem/Containers";
import SearchBar from "@shared/designSystem/SearchBar";
import designSystemStyles from "@shared/styles/designSystemStyles";

import {
    ExportNovedadesEmployeeListItem,
    useExportNovedadesEmployeesStepViewModel,
} from "./ExportNovedadesEmployeesStepScreenViewModel";

interface ExportNovedadesEmployeesStepScreenProps {
    process: NovedadesExportServiceModel.ProcessRecord;
    service: NovedadesExportService;
}

const baseGridColumns = "minmax(220px, 1.8fr) minmax(90px, 0.7fr) minmax(140px, 1fr) minmax(120px, 0.9fr)";
const statusColumnWidth = "minmax(120px, 0.85fr)";

const ExportNovedadesEmployeesStepScreen: React.FC<ExportNovedadesEmployeesStepScreenProps> = ({
    process,
    service,
}) => {
    const viewModel = useExportNovedadesEmployeesStepViewModel({
        processID: process.processID,
        organizationID: process.organizationID,
        service,
    });
    const gridTemplateColumns = [
        baseGridColumns,
        ...viewModel.statusColumns.map(() => statusColumnWidth),
    ].join(" ");
    const tableMinWidth = `${570 + (viewModel.statusColumns.length * 130)}px`;

    if (viewModel.loading) {
        return (
            <QontyLoading
                text={"Cargando colaboradores...".localized()}
                withQonty={true}
            />
        );
    }

    if (viewModel.error) {
        return (
            <QontyEmptyState
                text={viewModel.error}
                darkMode={false}
            />
        );
    }

    return (
        <VerticalContainer
            height="100%"
            gap={designSystemStyles.gapMd}
            margin="0"
            paddingTop={designSystemStyles.paddingMd}
        >
            <HorizontalContainer justifyContent="space-between" width="100%">
                <SearchBar
                    placeholder={"Buscar colaborador".localized()}
                    value={viewModel.searchField}
                    onChange={viewModel.setSearchField}
                />
                <RefreshButton text="" onClick={() => void viewModel.refresh()} />
            </HorizontalContainer>
            {viewModel.rows.length === 0 ? (
                <VerticalContainer width="100%" height="100%" alignment="center" justifyContent="center">
                    <QontyEmptyState
                        text={"No hay colaboradores para mostrar.".localized()}
                        darkMode={false}
                    />
                </VerticalContainer>
            ) : (
                <>
                    <div style={{ overflowX: "auto", width: "100%" }}>
                        <div style={{ minWidth: tableMinWidth }}>
                            <TableHeaderContainer
                                height={designSystemStyles.sizeRowMedium}
                                width="100%"
                                gridTemplateColumns={gridTemplateColumns}
                            >
                                <TableHeaderCell text={"Colaborador".localized()} width="100%" />
                                <TableHeaderCell text={"Legajo".localized()} width="100%" />
                                <TableHeaderCell text={"Horas trabajadas".localized()} width="100%" justifyContent="right" />
                                <TableHeaderCell text={"Horas extra".localized()} width="100%" justifyContent="right" />
                                {viewModel.statusColumns.map((column) => (
                                    <TableHeaderCell
                                        key={column.totalsField}
                                        text={column.label.localized()}
                                        width="100%"
                                        justifyContent="right"
                                        truncate={true}
                                    />
                                ))}
                            </TableHeaderContainer>
                        </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: designSystemStyles.gapNone, overflow: "auto", flex: 1, minHeight: 0, width: "100%" }}>
                        <div style={{ minWidth: tableMinWidth }}>
                            {viewModel.rows.map((row: ExportNovedadesEmployeeListItem) => (
                                <TableRowContainer
                                    key={row.id}
                                    width="100%"
                                    gridTemplateColumns={gridTemplateColumns}
                                >
                                    <TableCell text={"**"+row.name.trim()+"**"} width="100%" truncate={true} />
                                    <TableCell text={row.legajo} width="100%" truncate={true} />
                                    <TableCell text={row.workedHours} width="100%" justifyContent="right" truncate={true} />
                                    <TableCell text={row.extraHours} width="100%" justifyContent="right" truncate={true} />
                                    {viewModel.statusColumns.map((column) => (
                                        <TableCell
                                            key={column.totalsField}
                                            text={row.statusTotals[column.totalsField] ?? "0"}
                                            width="100%"
                                            justifyContent="right"
                                            truncate={true}
                                        />
                                    ))}
                                </TableRowContainer>
                            ))}
                        </div>
                    </div>
                    <div style={{ overflowX: "auto", width: "100%" }}>
                        <div style={{ minWidth: tableMinWidth }}>
                            <TableRowContainer
                                backgroundColor={designSystemStyles.colorBackgroundGray3}
                                width="100%"
                                gridTemplateColumns={gridTemplateColumns}
                            >
                                <TableCell text={`**${viewModel.totalsRow.label}**`} width="100%" truncate={true} />
                                <TableCell text={viewModel.totalsRow.legajo} width="100%" truncate={true} />
                                <TableCell text={`**${viewModel.totalsRow.workedHours}**`} width="100%" justifyContent="right" truncate={true} />
                                <TableCell text={`**${viewModel.totalsRow.extraHours}**`} width="100%" justifyContent="right" truncate={true} />
                                {viewModel.statusColumns.map((column) => (
                                    <TableCell
                                        key={column.totalsField}
                                        text={`**${viewModel.totalsRow.statusTotals[column.totalsField] ?? "0"}**`}
                                        width="100%"
                                        justifyContent="right"
                                        truncate={true}
                                    />
                                ))}
                            </TableRowContainer>
                        </div>
                    </div>
                </>
            )}
        </VerticalContainer>
    );
};

export default ExportNovedadesEmployeesStepScreen;
